import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { resolveAppVersion } from '@app/constants';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { UserService } from '@services/user.service';
import { getTranslation } from '@shared/translate-helper';
import { MessageService } from 'primeng/api';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, delay, finalize, map, retryWhen, scan, shareReplay, switchMap, take, tap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private maxRefreshRetries = 2;
  private refreshTokenInProgress$: Observable<string> | null = null;

  constructor(
    private readonly messageService: MessageService,
    private injector: Injector,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If the request targets our API
    if (req.url.includes(environment.apiUrl)) {
      // Add app version header
      const versionHeader = resolveAppVersion('localhost');
      req = req.clone({
        setHeaders: { 'x-medullar-version': versionHeader },
      });

      const authRequests = new Set([
        'login',
        'forgot-password',
        'reset-password',
        'verify-email',
        'register',
        'refresh',
      ]);

      const pathOnly = req.url.split('?')[0];
      const segments = pathOnly.split('/');
      const isAuthRequest = segments.some((segment) => authRequests.has(segment));

      if (isAuthRequest) {
        return next.handle(req);
      }

      // If is not for login/refresh endpoints, add the JWT token.
      const authService = this.injector.get(AuthService);
      const token = authService.getJwtToken();
      if (token) {
        // Check if token is expired before making the request
        if (authService.isTokenExpired(token)) {
          // Token is expired, attempt to refresh before proceeding
          return this.handleTokenRefresh(req, next);
        }
        req = this.addToken(req, token);
      }
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // If unauthorized, try to refresh token (unless the refresh endpoint itself failed)
        if (error.status === 401 && !req.url.includes('/auth/refresh')) {
          return this.handleUnauthorizedError(req, next, error);
        }
        this.handleHttpErrors(error);
        return throwError(() => error);
      }),
    );
  }

  private handleUnauthorizedError(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    // If the error code indicates a change in critical credentials, force logout.
    const errorCodes = ['role_changed', 'password_changed', 'company_changed'];
    if (error.error?.code && errorCodes.includes(error.error.code)) {
      const authService = this.injector.get(AuthService);
      authService.doLogout();
      return EMPTY;
    }

    return this.handleTokenRefresh(req, next);
  }

  private handleTokenRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If no refresh is in progress, start one and share the result.

    const authService = this.injector.get(AuthService);
    const userService = this.injector.get(UserService);

    if (!authService.isLoggedIn) return throwError(() => new Error('User is not logged in'));

    if (!this.refreshTokenInProgress$) {
      this.refreshTokenInProgress$ = authService.refreshToken().pipe(
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, err) => {
              if (retryCount >= this.maxRefreshRetries || err.status !== 401) {
                throw err;
              }
              return retryCount + 1;
            }, 0),
            delay(1000),
          ),
        ),
        tap((newToken: any) => {
          // Update stored token and user profile on successful refresh.
          authService.setJwtToken(newToken.access);
          userService
            .getUserProfile(newToken.user.uuid)
            .pipe(take(1))
            .subscribe({
              next: (res) => userService.setUserData(res),
              error: () => authService.doLogout(),
            });
        }),
        map((newToken: any) => newToken.access),
        finalize(() => {
          // Reset the refresh observable when complete.
          this.refreshTokenInProgress$ = null;
        }),
        shareReplay(1),
      );
    }
    // Once the refresh token observable emits, retry the failed request with the new token.
    return this.refreshTokenInProgress$.pipe(
      switchMap((token: string) => next.handle(this.addToken(req, token))),
      catchError((err) => {
        // If the refresh token fails, force logout.
        if (err.status === 401 || err.status === 400) {
          authService.doLogout();
        }
        return throwError(err);
      }),
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Handles HTTP errors and routes the user to the appropriate error page.
   */
  private handleHttpErrors(err: HttpErrorResponse): void {
    const translateService = this.injector.get(TranslateService);
    const keysByStatus: Record<number, string[]> = {
      [HttpError.NotFound]: ['not_found_error', 'not_found_error_description'],
      [HttpError.TimeOut]: ['timeout_error', 'timeout_error_description'],
      [HttpError.BadRequest]: ['bad_request_error', 'bad_request_error_description'],
      [HttpError.Forbidden]: ['access_forbidden', 'access_forbidden_description'],
      [HttpError.Unauthorized]: ['access_forbidden', 'access_forbidden_description'],
      [HttpError.TooManyRequests]: ['too_many_requests', 'too_many_requests_description'],
      [HttpError.InternalServerError]: ['server_error', 'server_error_description'],
      [HttpError.BadGateway]: ['server_error', 'server_error_description'],
      [HttpError.ServiceUnavailable]: ['server_error', 'server_error_description'],
    };

    const translationKeys = keysByStatus[err.status];
    if (!translationKeys) {
      throw err;
    }

    getTranslation(translateService, translationKeys).subscribe((t: any) => {
      this.messageService.add({
        severity: 'error',
        summary: t[translationKeys[0]],
        detail: t[translationKeys[1]],
        life: 10000,
      });
    });
    throw err;
  }
}

export class HttpError {
  static readonly BadRequest = 400;
  static readonly Unauthorized = 401;
  static readonly Forbidden = 403;
  static readonly NotFound = 404;
  static readonly TimeOut = 408;
  static readonly TooManyRequests = 429;
  static readonly InternalServerError = 500;
  static readonly BadGateway = 502;
  static readonly ServiceUnavailable = 503;
}
